import ToastManager from "../toastManager";
import TokenManager from "../tokenManager";
import i18n from "translations/config";
import { ERROR_MESSAGES, ERROR_REASONS, ERROR_RESOURCES, REQUEST_ABORT_CODE, RESPONSE_STATE } from "./constants";
import SessionStorageUtils from "utils/sessionStorage";
import { logout } from "utils/auth";
import { setMaintenanceModeAction } from "store/slices/auth/actions";
import { setBlockedMessageAction } from "store/slices/common/actions";
import isString from "utils/typeChecks/isString";



class ResponseManager {
	static dispatch = null;
	static _instance = null;

	static get instance() {
		return ResponseManager._instance || new ResponseManager();
	}

	constructor() {
		if (ResponseManager._instance) {
			return ResponseManager._instance;
		}

		ResponseManager._instance = this;
	}

	#parseErrorMessage({ reason, resource }) {
		const errorMessage = i18n.t(`errors.message.${reason}`);
		const errorResource =
			resource !== ERROR_RESOURCES.NONE ? i18n.t(`errors.resources.${resource}`) : "";

		return errorMessage?.replace("%resource%", errorResource);
	}

	#processUnauthorizedError({ requestConfig, responseStatus }) {
		if (responseStatus === 401) {
			const pendingPromise = TokenManager.instance.registerPendingRequest(requestConfig);

			if (!TokenManager.instance.isTokenRefreshing) {
				const isStream = Boolean(requestConfig.headers["stream"])
				TokenManager.instance.updateTokenAsync(isStream);
			}

			return pendingPromise;
		}

		if (responseStatus === 400) {
			logout();
		}
	}

	#processMaintenanceMode() {
		if (!ResponseManager.dispatch) {
			return;
		}

		SessionStorageUtils.remove("player");

		ResponseManager.dispatch(setMaintenanceModeAction());
	}

	#processNotAllowedError({ errorResource, errorMessage }) {
		if (!ResponseManager.dispatch) {
			ToastManager.instance.error(errorMessage);
			return;
		}

		if ([ERROR_RESOURCES.PLAYER, ERROR_RESOURCES.PLAYER_GEO_LOCATION].includes(errorResource)) {
			ResponseManager.dispatch(setBlockedMessageAction("errors.message.Blocked"));
			return;
		}

		if (ERROR_RESOURCES.SESSION === errorResource && [ERROR_MESSAGE.NOT_SUPPORTED_CURRENCY, ERROR_MESSAGE.INVALID_PLAYER_CURRENCY].includes(errorMessage)) {
			ResponseManager.dispatch(setBlockedMessageAction("errors.message.CurencyBlocked"));
			return;
		}

		if (ERROR_RESOURCES.PLAYER_DEVICE === errorResource && ERROR_MESSAGE.BLOCKED === errorMessage) {
			ResponseManager.dispatch(setBlockedMessageAction("errors.message.DeviceBlocked"));
			return;
		}

		if (ERROR_RESOURCES.PLAYER_BROWSER === errorResource && ERROR_MESSAGE.BLOCKED === errorMessage) {
			ResponseManager.dispatch(setBlockedMessageAction("errors.message.DeviceNotSupported"));
			return;
		}

		if (ERROR_RESOURCES.EVENT === errorResource && ERROR_MESSAGE.IMPERMISSIBLE_EVENT_STATE === errorMessage) {
			ToastManager.instance.error(errorMessage);
			return;
		}

		ToastManager.instance.error(errorMessage);
	}

	#processInvalidParametersError({ errorResource, errorMessage }) {

		if (errorResource === ERROR_RESOURCES.PLAYER_GEO_LOCATION) {
			ResponseManager.dispatch(setBlockedMessageAction("errors.message.Blocked"));
			return;
		}

		if (errorResource === ERROR_RESOURCES.PLAYER_SETTINGS && errorMessage === ERROR_MESSAGE.PLAYER_SETTINGS_INVALID_PARAMETER) {
			ToastManager.instance.error(
				i18n.t(`errors.message.InvalidParameters`)
					.replace("%resource%", i18n.t(`errors.resources.${ERROR_RESOURCES.PLAYER_SETTINGS}`))
			);
			return;
		}

		if (errorResource === ERROR_RESOURCES.CUSTOM && isString(errorMessage)) {
			try {
				const parsedMsg = JSON.parse(message);
				const { messageKey, parameters: { minAmount, maxAmount } } = parsedMsg;
				ToastManager.instance.error(i18n.t(`errors.message.${messageKey}`).replace("%minAmount%", minAmount).replace("%maxAmount%", maxAmount));
			} catch (error) {
				console.error(error);
			}
			return;
		}

		if (
			errorResource === ERROR_RESOURCES.PLAYER_GEO_LOCATION &&
			errorMessage === ERROR_MESSAGES.PLAYER_SETTINGS_INVALID_PARAMETER
		) {
			const msg = this.#parseErrorMessage({
				reason: ERROR_REASONS.INVALID_PARAMETERS,
				resource: ERROR_RESOURCES.PLAYER_SETTINGS
			})
			if (msg) {
				ToastManager.instance.error(msg);
			}
			return
		}

		let parsedMessage = this.#parseErrorMessage({
			response: ERROR_REASONS.INVALID_PARAMETERS,
			resource: errorResource
		});

		if (errorResource === ERROR_RESOURCES.OPERATOR) {
			if(errorMessage === "Player does not have enough funds to process an action.") {
				parsedMessage = i18n.t("errors.message.NotEnoughBalance");
			} else if (errorMessage === " Operator message is:  Not enough balance") {
				parsedMessage = i18n.t(`errors.message.${ERROR_REASONS.NOT_ENOUGH_BALANCE}`);
			} else {
				parsedMessage = i18n.t(`errors.message.${errorMessage}`);
			}
			return ResponseManager.dispatch(setErrorMessageAction(parsedMessage));
		}

		if (errorResource !== ERROR_RESOURCES.BET_SLIP_BET) {
			parsedMessage += errorMessage ?? "";
		}

		ToastManager.instance.error(parsedMessage);
	}

	#processInvalidPlayerDataError() {
		if (!ResponseManager.dispatch) {
			return;
		}

		return ResponseManager.dispatch(setBlockedMessageAction("errors.message.Blocked"));
	}

	processSuccess(response) {
		const responseStatus = response.data.status;

		if (responseStatus !== RESPONSE_STATE.SUCCESS) {
			this.processError({ response });
		}

		return response;
	}

	processError(error) {
		const { response, config: requestConfig, code } = error;
		const { data: errorData = {}, status: responseStatus } = response ?? {};
		const { message: errorMessage = "", status: errorReason = "", resource: errorResource = "" } = errorData;

		if (errorReason === ERROR_REASONS.UNAUTHORIZED) {
			return this.#processUnauthorizedError({ requestConfig, responseStatus });
		} else if (errorReason === ERROR_REASONS.MAINTENANCE_MODE_ON) {
			this.#processMaintenanceMode();
		} else if (errorReason === ERROR_REASONS.NOT_ALLOWED) {
			this.#processNotAllowedError({ errorResource, errorMessage });
		} else if (errorReason === ERROR_REASONS.INVALID_PARAMETERS) {
			this.#processInvalidParametersError({ errorResource, errorMessage });
		} else if (errorMessage === ERROR_MESSAGES.INVALID_PLAYED_DATA) {
			this.#processInvalidPlayerDataError();
		} else {
			const message = Boolean(errorReason)
				? this.#parseErrorMessage({ reason: errorReason, resource: errorResource })
				: errorMessage;
			if (code !== REQUEST_ABORT_CODE) {
				if (!Boolean(message)) {
					return Promise.reject(errorData);
				}
				ToastManager.instance.error(message);
			}
		}

		return Promise.reject(error);
	}
}

export default ResponseManager;
